import React, { useState, Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition, Combobox } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import { Beat } from "../../../plugins/spinners.plugin";
import CLOSE from "../../../assets/images/icons/close.svg";
import Button from "../../buttons/buttons";
import Input from "../../inputs/inputs";
import { NIGERIAN_BANKS } from "../../../helpers/banks";
import { storeRevenuePayout } from "../../../redux/revenuePayout/revenuePayoutActions";
import { filterRevenueWallet } from "../../../redux/revenueWallet/revenueWalletActions";
import { filterRevenueSettingsAction } from "../../../redux/revenueSettings/revenueSettingsActions";

const initialInput = {
  revenue_wallet_id: "",
  amount: "",
  account_number: "",
  account_name: "",
};

/**
 * Request a payout for a business or shipper.
 * @param {string} ownerId - the business or shipper id
 * @param {"business" | "shipper"} type
 */
export function RequestPayoutModal({
  ownerId,
  type = "business",
  disabled,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const idField = type === "shipper" ? "shipper_id" : "business_id";
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");
  const [bank, setBank] = useState(null);
  const [input, setInput] = useState(initialInput);

  const { allRevenueWallet } = useSelector((state) => state.revenueWallet);
  const { allRevenueSettings } = useSelector((state) => state.revenueSettings);

  const filteredBanks =
    query === ""
      ? NIGERIAN_BANKS
      : NIGERIAN_BANKS.filter((item) =>
          item.name.toLowerCase().includes(query.toLowerCase()),
        );

  const selectedWallet = allRevenueWallet?.data?.find(
    (wallet) => wallet?.id === input.revenue_wallet_id,
  );
  const walletBalance = Number(selectedWallet?.balance || 0);
  const walletCurrency = selectedWallet?.currency_code || "";

  const canSubmit =
    selectedWallet &&
    Number(input.amount) <= walletBalance &&
    bank &&
    Number(input.amount) > 0 &&
    input.account_number.length === 10 &&
    input.account_name.trim().length > 0;

  useEffect(() => {
    if (!ownerId) return;
    dispatch(filterRevenueWallet({ [idField]: ownerId, type }));
    dispatch(filterRevenueSettingsAction({ [idField]: ownerId, type }));
  }, [dispatch, ownerId, idField, type]);

  function closeModal() {
    setIsOpen(false);
  }
  function openModal() {
    setIsOpen(true);
  }

  function resetForm() {
    setInput(initialInput);
    setBank(null);
    setQuery("");
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleAccountNumber = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setInput({ ...input, account_number: digits });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const response = await dispatch(
      storeRevenuePayout({
        type,
        [idField]: ownerId,
        revenue_wallet_id: input.revenue_wallet_id,
        amount: Number(input.amount),
        bank_code: bank.code,
        account_number: input.account_number,
        account_details: {
          bank_name: bank.name,
          account_number: input.account_number,
          account_name: input.account_name.trim(),
        },
      }),
    );
    setSubmitting(false);
    if (response?.status === "success") {
      resetForm();
      closeModal();
      onSuccess && onSuccess(response);
    }
  };

  return (
    <>
      <Button.Primary
        title={"Request Pay Out"}
        type={"button"}
        disabled={disabled}
        onClick={openModal}
      />
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-[415px] sm:max-w-[600px] bg-white dark:bg-[#121212] rounded-sm pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8">
                  <div className="absolute top-3 right-2 pt-4 pr-4">
                    <button
                      type="button"
                      className="bg-[#ffeae5] rounded-md sm:p-3 p-1"
                      onClick={closeModal}
                    >
                      <span className="sr-only">Close</span>
                      <img
                        src={CLOSE}
                        alt="close"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </button>
                  </div>
                  <div className="text-center sm:mt-4">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold text-black dark:text-white"
                    >
                      Request Pay Out
                    </Dialog.Title>
                    <div className="border-b-2 dark:border-[#BCBCBC] mt-4"></div>
                  </div>

                  <div className="px-4 sm:px-8 pt-8">
                    {allRevenueSettings?.data &&
                      allRevenueSettings?.data?.map((settings) => (
                        <div
                          key={settings?.id}
                          className="grid grid-cols-2 gap-4 p-4 border rounded-md"
                        >
                          <div>
                            <label>KYC Status</label>
                            <p></p>
                          </div>
                          <div>
                            <label>Balance</label>
                            <p className="font-bold text-md">
                              {selectedWallet
                                ? `${walletCurrency} ${walletBalance.toLocaleString(
                                    undefined,
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}`
                                : "Select a wallet"}
                            </p>
                          </div>
                          <div>
                            <label>Minimum Payout</label>
                            <p className="font-bold text-md">
                              {settings?.minimum_amount}
                            </p>
                          </div>
                          <div>
                            <label>Maximum Payout</label>
                            <p className="font-bold text-md">
                              {settings?.maximum_amount}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="p-4 sm:p-8">
                    <Input.Select
                      title={"Revenue Wallet"}
                      htmlFor={"revenue_wallet_id"}
                      name={"revenue_wallet_id"}
                      value={input.revenue_wallet_id}
                      onChange={handleChange}
                      placeholder={"Select revenue wallet"}
                    >
                      <option value="">Select revenue wallet</option>
                      {allRevenueWallet?.data?.length > 0 &&
                        allRevenueWallet?.data?.map((wallet, i) => (
                          <option key={i} value={wallet?.id}>
                            {wallet?.currency_name} ({wallet?.currency_code})
                          </option>
                        ))}
                    </Input.Select>

                    <Combobox as="div" value={bank} onChange={setBank}>
                      <Combobox.Label className="block text-sm font-medium text-[#151515] dark:text-white">
                        Bank Name
                      </Combobox.Label>
                      <div className="relative mt-2 mb-4">
                        <Combobox.Input
                          className="mt-2 block pl-3 pr-10 w-full text-base bg-[#FFFFFF] dark:bg-[transparent] focus:ring-2 focus:ring-red-600 focus:border-red-600 focus:outline-none sm:text-sm h-[40px] py-2 border border-gray-300"
                          placeholder={"Search bank"}
                          onChange={(event) => setQuery(event.target.value)}
                          displayValue={(item) => item?.name || ""}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                          <SelectorIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </Combobox.Button>

                        <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto bg-white dark:bg-[#1F1F1F] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {filteredBanks.length === 0 ? (
                            <div className="py-2 px-3 text-gray-500">
                              No bank found
                            </div>
                          ) : (
                            filteredBanks.map((item) => (
                              <Combobox.Option
                                key={item.code}
                                value={item}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                    active
                                      ? "bg-[#CE2600] text-white"
                                      : "text-[#151515] dark:text-white"
                                  }`
                                }
                              >
                                {({ active, selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? "font-semibold" : ""
                                      }`}
                                    >
                                      {item.name}
                                    </span>
                                    {selected && (
                                      <span
                                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                          active
                                            ? "text-white"
                                            : "text-[#CE2600]"
                                        }`}
                                      >
                                        <CheckIcon
                                          className="h-5 w-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))
                          )}
                        </Combobox.Options>
                      </div>
                    </Combobox>

                    <Input.Label
                      title={"Account Number"}
                      htmlFor={"account_number"}
                      id={"account_number"}
                      name={"account_number"}
                      type={"text"}
                      autoComplete={"off"}
                      value={input.account_number}
                      placeholder={"10-digit account number"}
                      onChange={handleAccountNumber}
                    />

                    <Input.Label
                      title={"Account Name"}
                      htmlFor={"account_name"}
                      id={"account_name"}
                      name={"account_name"}
                      type={"text"}
                      autoComplete={"off"}
                      value={input.account_name}
                      placeholder={"Name on the account"}
                      onChange={handleChange}
                    />

                    <Input.Number
                      title={`Amount${walletCurrency ? ` (${walletCurrency})` : ""}`}
                      htmlFor={"amount"}
                      id={"amount"}
                      name={"amount"}
                      min={1}
                      max={walletBalance || undefined}
                      value={input.amount}
                      placeholder={"Enter amount"}
                      onChange={handleChange}
                    />
                    {selectedWallet && Number(input.amount) > walletBalance && (
                      <p className="text-sm text-red-600 -mt-2">
                        Amount is more than the wallet balance
                      </p>
                    )}

                    <div className="flex gap-8 mt-5 w-full">
                      <Button.Secondary
                        title={"Back"}
                        onClick={closeModal}
                        className="w-full"
                      />
                      <Button.Primary
                        title={
                          submitting ? <Beat color={"#ffffff"} /> : "Submit"
                        }
                        disabled={!canSubmit || submitting}
                        type={"button"}
                        className="w-full"
                        onClick={handleSubmit}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
